import { useEffect, useState } from 'react';
import {
    BudgetProposalWithResourcesT,
    CadrageConfigT,
    CadrageT,
} from 'routes/BudgetModule/config/types';
import { getExercicesOfBudget } from 'routes/BudgetModule/components/shared/BudgetLibrary/helper';
import { useTranslation } from 'react-i18next';
import { getAvailableCadrageMetrics } from './helper';
import { useDispatch } from 'store/hooks';
import {
    useMutation,
    useQueryClient,
    UseQueryResult,
} from '@tanstack/react-query';
import { createBudgetQueryKeys } from 'routes/BudgetModule/helpers/createBudgetQueryKeys';
import { HttpError, RQAPICall, RQAPICallWithData } from 'store/APIClient';
import { snakify } from 'helpers/syntaxHelper';
import { getCadrageConfigPostProcessingMiddleware } from 'routes/BudgetModule/shared/getCadrageMiddlewares';
import { useCadrageQuery } from 'routes/BudgetModule/helpers/hooks/useCustomQuery';
import { AvailableCadrageMetricsT, CadrageSummaryT } from './type';
import { translateBlockValue } from 'i18n';
import { CHAPTER_SOFTWARE_ID } from 'routes/BudgetModule/config/constants';

type useCadrageExerciceSelectionReturnT = {
    exercice: number | null;
    setExercice: DispatchState<number | null>;
    sortedExercices: number[];
};

type useCadrageMetricSelectionReturnT = {
    metric: AvailableCadrageMetricsT | null;
    availableMetrics: AvailableCadrageMetricsT[];
};

/**
 * Custom hook to fetch the cadrage configuration for a specific exercise (year).
 *
 */
const useGetCadrageConfig = (
    exercice: number | null,
): UseQueryResult<CadrageConfigT, HttpError> => {
    const { cadrageKeys } = createBudgetQueryKeys();
    return useCadrageQuery(
        cadrageKeys({ exercice: exercice ?? Number.NaN }),
        async () => {
            const response = await RQAPICallWithData<CadrageConfigT>({
                endpoint: `budget/cadrage/${exercice}`,
                method: 'GET',
                postProcessingCallback:
                    getCadrageConfigPostProcessingMiddleware,
            });

            return response.content.data;
        },
        { enabled: exercice != null },
    );
};

/**
 * Custom hook to manage the exercice selection.
 */
export const useCadrageExerciceSelection = (
    budgetProposals: BudgetProposalWithResourcesT[],
): useCadrageExerciceSelectionReturnT => {
    const [exercice, setExercice] = useState<number | null>(null);
    const exercices = getExercicesOfBudget(budgetProposals);
    const sortedExercices = [...exercices].sort((a, b) => b - a);

    useEffect(() => {
        if (sortedExercices.length > 0) {
            setExercice(sortedExercices[0]);
        } else {
            setExercice(null);
        }
    }, [budgetProposals]);

    return {
        exercice,
        setExercice,
        sortedExercices,
    };
};

/**
 * Custom hook to manage the metric selection.
 */
export const useCadrageMetricSelection = (
    exercice: number | null,
): useCadrageMetricSelectionReturnT => {
    const { t } = useTranslation();
    const [metric, setMetric] = useState<AvailableCadrageMetricsT | null>(null);
    const metricMap = getAvailableCadrageMetrics(exercice, t);
    const { data: cadrageConfig } = useGetCadrageConfig(exercice);

    useEffect(() => {
        const currentMetric = metricMap.find(
            (availableCadrageMetric) =>
                availableCadrageMetric.metric === cadrageConfig?.metric &&
                availableCadrageMetric.yearOffset === cadrageConfig?.yearOffset,
        );
        setMetric(currentMetric ? currentMetric : null);
    }, [cadrageConfig]);

    return {
        metric,
        availableMetrics: metricMap,
    };
};

/**
 * Custom hook to update the cadrage configuration for a specific exercice.
 */
export const useUpdateCadrageConfig = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { cadrageKeys } = createBudgetQueryKeys();

    return useMutation({
        mutationFn: async (newCadrageConfig: CadrageConfigT) => {
            const response = await RQAPICallWithData<CadrageConfigT>({
                endpoint: `budget/cadrage/${newCadrageConfig.exercice}`,
                method: 'PUT',
                body: {
                    metric: snakify(newCadrageConfig.metric),
                    yearOffset: newCadrageConfig.yearOffset,
                },
                postProcessingCallback:
                    getCadrageConfigPostProcessingMiddleware,
            });
            return response.content.data;
        },

        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [cadrageKeys()[0]],
            });
            dispatch.vision.notifyUser({
                message: t('adminBudgetSpace.cadrage.successSnackBar'),
                snackType: 'success',
            });
        },

        meta: {
            useGlobalOnError: true,
        },
    });
};

/**
 * Custom hook to fetch the list of bps name with custom cadrage for a specific exercise.
 *
 */
export const useGetBpWithCustomCadrageNames = (
    exercice: number | null,
): UseQueryResult<string[], HttpError> => {
    const { cadrageKeys } = createBudgetQueryKeys();
    return useCadrageQuery(
        cadrageKeys({
            exercice: exercice,
            searchCustomCadrage: true,
        }),
        async () => {
            const response = await RQAPICallWithData<{ name: string }[]>({
                endpoint: `budget/cadrage/budget_proposals_with_custom_cadrage/${exercice}`,
                method: 'GET',
            });

            return response.content.data.map((bp) => bp.name);
        },
        { enabled: exercice != null },
    );
};

/**
 * Custom hook to fetch the cadrage summary for a budget proposal.
 *
 */
export const useGetCadrageSummary = (
    budgetProposalId: number,
    enabled: boolean,
): UseQueryResult<CadrageSummaryT, HttpError> => {
    const { cadrageKeys } = createBudgetQueryKeys();
    return useCadrageQuery(
        cadrageKeys({
            budgetProposalId,
            searchSummary: true,
        }),
        async () => {
            const response = await RQAPICallWithData<CadrageSummaryT>({
                endpoint: `budget/cadrage/summary/${budgetProposalId}`,
                method: 'GET',
            });

            return response.content.data;
        },
        { enabled: enabled },
    );
};

/**
 * Custom hook to edit a custom cadrage for a budget proposal.
 *
 */
export const useUpdateCustomCadrage = (
    budgetProposal: BudgetProposalWithResourcesT,
) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { cadrageKeys } = createBudgetQueryKeys();
    return useMutation({
        mutationFn: async ({
            customCadrage,
        }: {
            customCadrage: CadrageT[];
        }) => {
            await RQAPICall({
                endpoint: `budget/cadrage/custom/${budgetProposal.id}`,
                method: 'POST',
                body: customCadrage,
            });
        },

        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [cadrageKeys()[0]],
            });
            const { budgetProposalsKeys } = createBudgetQueryKeys();
            queryClient.setQueryData(
                budgetProposalsKeys({ withResources: true }),
                (oldData: BudgetProposalWithResourcesT[]) =>
                    oldData.map((oldBudgetProposal) =>
                        oldBudgetProposal.id === budgetProposal.id
                            ? {
                                  ...oldBudgetProposal,
                                  hasCustomCadrage: true,
                              }
                            : oldBudgetProposal,
                    ),
            );
            dispatch.vision.notifyUser({
                message: t('adminBudgetSpace.cadrage.customSuccessSnackBar'),
                snackType: 'success',
            });
        },

        meta: {
            useGlobalOnError: true,
        },
    });
};

/**
 * Custom hook to delete a custom cadrage by its bp.
 */
export const useDeleteCustomCadrage = (
    budgetProposal: BudgetProposalWithResourcesT,
) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async () => {
            await RQAPICall({
                endpoint: `budget/cadrage/custom/${budgetProposal.id}`,
                method: 'DELETE',
            });
        },

        onSuccess: () => {
            const { cadrageKeys } = createBudgetQueryKeys();
            const { budgetProposalsKeys } = createBudgetQueryKeys();
            queryClient.invalidateQueries({
                queryKey: [cadrageKeys()[0]],
            });
            queryClient.setQueryData(
                budgetProposalsKeys({ withResources: true }),
                (oldData: BudgetProposalWithResourcesT[]) =>
                    oldData.map((oldBudgetProposal) =>
                        oldBudgetProposal.id === budgetProposal.id
                            ? {
                                  ...oldBudgetProposal,
                                  hasCustomCadrage: false,
                              }
                            : oldBudgetProposal,
                    ),
            );
            dispatch.vision.notifyUser({
                message: t(
                    'adminBudgetSpace.cadrage.deletedCustomSuccessSnackBar',
                ),
                snackType: 'success',
            });
        },

        meta: {
            useGlobalOnError: true,
        },
    });
};

/**
 * Custom hook to toggle the cadrage by chapter value for a budget proposal.
 */
export const useUpdateCadrageByChapter = (budgetProposalId?: number) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async () => {
            const response = await RQAPICallWithData<
                Pick<BudgetProposalWithResourcesT, 'cadrageByChapter'>
            >({
                endpoint: `budget/cadrage/by_chapter/${budgetProposalId}`,
                method: 'PATCH',
            });
            return response.content.data.cadrageByChapter;
        },

        onSuccess: (newValue) => {
            const { cadrageKeys } = createBudgetQueryKeys();
            const { budgetProposalsKeys } = createBudgetQueryKeys();
            queryClient.invalidateQueries({
                queryKey: [cadrageKeys()[0]],
            });
            queryClient.setQueryData(
                budgetProposalsKeys({ withResources: true }),
                (oldData: BudgetProposalWithResourcesT[]) =>
                    oldData.map((oldBudgetProposal) =>
                        oldBudgetProposal.id === budgetProposalId
                            ? {
                                  ...oldBudgetProposal,
                                  cadrageByChapter: newValue,
                              }
                            : oldBudgetProposal,
                    ),
            );
            dispatch.vision.notifyUser({
                message: t(
                    newValue
                        ? 'adminBudgetSpace.cadrage.trueCadrageByChapterSuccessSnackBar'
                        : 'adminBudgetSpace.cadrage.falseCadrageByChapterSuccessSnackBar',
                    {
                        chapter: translateBlockValue(
                            CHAPTER_SOFTWARE_ID,
                            true,
                        ).toLowerCase(),
                    },
                ),
                snackType: 'success',
            });
        },

        meta: {
            useGlobalOnError: true,
        },
    });
};
